// Shared bill sort/grouping logic, extracted from Legislation.js so the Legislation list,
// report previews, and PDF/Word exports all reproduce identical ordering and Calendar-view
// date/location group headers from one source of truth.

export function sortBillsByMode(bills, sortMode) {
  if (sortMode === 'file_location') {
    const billsCopy = [...bills];
    return billsCopy.sort((a, b) => {
      const getFileData = (bill) => {
        const rawLoc = bill.DailyFile?.file_location || '';
        // Check for "A-" prefix to determine if it's effectively an inactive file number for sorting
        const isInactive = rawLoc.trim().toUpperCase().startsWith('A-');

        // Remove "A-" and parse to integer
        const cleanLoc = rawLoc.replace(/^A-\s*/i, '');
        const fileNum = parseInt(cleanLoc, 10);

        return {
          num: isNaN(fileNum) ? 999999 : fileNum,
          isInactive
        };
      };

      const aData = getFileData(a);
      const bData = getFileData(b);

      // Sort non-Inactive first
      if (aData.isInactive !== bData.isInactive) return aData.isInactive ? 1 : -1;

      // Then sort by numerical value
      return aData.num - bData.num;
    });
  }

  if (sortMode === 'hearing_date') {
    const billsCopy = [...bills];
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

    return billsCopy.sort((a, b) => {
      const getNextHearingDate = (bill) => {
        const upcoming = bill.CommitteeHearings?.find(h =>
          h.committee_code === bill.location && (h.hearing_date || '').split('T')[0] >= todayStr
        );
        // Return date string if found, otherwise Infinity to push bills without hearings to the bottom
        return upcoming ? (upcoming.hearing_date || '').split('T')[0] : Infinity;
      };

      const dateA = getNextHearingDate(a);
      const dateB = getNextHearingDate(b);

      // Primary sort: Hearing Date (Soonest first)
      // Both Infinity: equal; one Infinity: push it to the bottom; both strings: lex compare works for YYYY-MM-DD
      if (dateA !== dateB) {
        if (dateA === Infinity) return 1;
        if (dateB === Infinity) return -1;
        return dateA < dateB ? -1 : 1;
      }

      // Secondary sort: Location Description
      const locA = a.LocationCode?.description || a.location || '';
      const locB = b.LocationCode?.description || b.location || '';
      return locA.localeCompare(locB);
    });
  }

  if (sortMode === 'calendar') {
    const billsCopy = [...bills];
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    const getFloorFileNum = (fileLoc) => {
      if (!fileLoc) return 999999;
      const isInactive = fileLoc.toUpperCase().startsWith('A-');
      const num = parseInt(fileLoc.replace(/^A-\s*/i, ''), 10);
      return isNaN(num) ? 999999 : (isInactive ? 1e6 + num : num);
    };
    return billsCopy.sort((a, b) => {
      const getCalendarKey = (bill) => {
        const upcoming = bill.CommitteeHearings?.find(h =>
          h.committee_code === bill.location && (h.hearing_date || '').split('T')[0] >= todayStr
        );
        if (upcoming) {
          return { date: (upcoming.hearing_date || '').split('T')[0], loc: bill.LocationCode?.description || bill.location || '', fileNum: null };
        }
        const locCode = bill.DailyFile?.location_code || '';
        const floorDate = bill.DailyFile?.publication_date ? (bill.DailyFile.publication_date || '').split('T')[0] : null;
        if (floorDate && floorDate >= todayStr && (locCode.startsWith('AF') || locCode.startsWith('SF'))) {
          return {
            date: floorDate,
            loc: locCode.startsWith('AF') ? 'Assembly Floor' : 'Senate Floor',
            fileNum: getFloorFileNum(bill.DailyFile?.file_location),
          };
        }
        return { date: '9999-99-99', loc: bill.LocationCode?.description || bill.location || '', fileNum: null };
      };
      const aKey = getCalendarKey(a);
      const bKey = getCalendarKey(b);
      if (aKey.date !== bKey.date) return aKey.date < bKey.date ? -1 : 1;
      if (aKey.loc !== bKey.loc) return aKey.loc.localeCompare(bKey.loc);
      if (aKey.fileNum !== null && bKey.fileNum !== null) return aKey.fileNum - bKey.fileNum;
      return 0;
    });
  }

  return bills;
}

// Pre-computes which bills need a calendar date/location header, in sorted order.
// Returns an empty Map when sortMode isn't 'calendar'.
export function computeCalendarHeaders(sortedBills, sortMode) {
  if (sortMode !== 'calendar') return new Map();
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const map = new Map();
  let lastDate = null;
  let lastLoc = null;
  sortedBills.forEach(bill => {
    const upcoming = bill.CommitteeHearings?.find(h =>
      h.committee_code === bill.location && (h.hearing_date || '').split('T')[0] >= todayStr
    );
    let dateKey = null;
    let locStr = bill.LocationCode?.description || bill.location || '';
    if (upcoming) {
      dateKey = (upcoming.hearing_date || '').split('T')[0];
    } else {
      const locCode = bill.DailyFile?.location_code || '';
      const floorDate = bill.DailyFile?.publication_date ? (bill.DailyFile.publication_date || '').split('T')[0] : null;
      if (floorDate && floorDate >= todayStr && (locCode.startsWith('AF') || locCode.startsWith('SF'))) {
        dateKey = floorDate;
        locStr = locCode.startsWith('AF') ? 'Assembly Floor' : 'Senate Floor';
      }
    }
    const groupDate = dateKey || 'no-hearing';
    const showDateHeader = groupDate !== lastDate;
    const showLocHeader = !!dateKey && (showDateHeader || locStr !== lastLoc);
    const isFirstLocUnderDate = showLocHeader && showDateHeader;
    if (showDateHeader) { lastDate = groupDate; lastLoc = locStr; }
    else if (showLocHeader) { lastLoc = locStr; }
    map.set(bill.bill_id, {
      showDateHeader,
      headerDateStr: showDateHeader
        ? (dateKey
            ? new Date(dateKey + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
            : 'No Upcoming Hearing')
        : null,
      showLocHeader,
      isFirstLocUnderDate,
      headerLocStr: locStr,
    });
  });
  return map;
}
